/**
 * カスタム項目(hr:admin)。会社ごとに違う従業員の欄(エンド顧客・就業場所・所属会社など)を
 * 管理者が定義する。値は従業員の登録・編集フォームに欄として出て、詳細・一覧(指定した項目)・
 * Excel 出力・CSV 取込(見出し=項目名)に乗る。型は作った後に変えられない。
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";

import type { CustomField, CustomFieldInput } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText, useCustomFields, useHrPerms } from "./hooks";

const TYPE_LABEL: Record<CustomField["type"], string> = {
  text: "文字",
  select: "選択肢",
  date: "日付",
};

type FormValues = {
  label: string;
  type: CustomField["type"];
  options?: string[];
  required: boolean;
  show_in_list: boolean;
  searchable: boolean;
  sort: number;
};

export function CustomFieldsPage() {
  const api = useApi();
  const qc = useQueryClient();
  const { canAdmin } = useHrPerms();
  const [editing, setEditing] = useState<CustomField | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const type = Form.useWatch("type", form);
  const query = useCustomFields(false);

  const open = creating || editing !== null;
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (editing)
      form.setFieldsValue({
        label: editing.label,
        type: editing.type,
        options: editing.options,
        required: editing.required,
        show_in_list: editing.show_in_list,
        searchable: editing.searchable,
        sort: editing.sort,
      });
  }, [open, editing, form]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["hr"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  };
  const close = () => {
    setCreating(false);
    setEditing(null);
  };

  // 保存対象の印(編集なら id、新規なら "new")。成功時に閉じるのは、その対象をまだ開いているときだけ
  // (保存中に「やめる」→別の項目を開くと、前の保存の成功で新しいフォームが閉じていた)
  const targetKey = editing ? String(editing.id) : "new";
  const save = useMutation({
    mutationFn: ({ v, target }: { v: FormValues; target: string }) => {
      const body: CustomFieldInput = {
        label: v.label,
        required: v.required,
        show_in_list: v.show_in_list,
        searchable: v.searchable,
        sort: v.sort,
      };
      if (v.type === "select") body.options = v.options ?? [];
      if (target !== "new") return api.updateCustomField(Number(target), body);
      return api.createCustomField({ ...body, type: v.type });
    },
    onSuccess: (_row, { target }) => {
      void message.success(target === "new" ? "作成した" : "更新した");
      if (open && target === targetKey) close();
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      api.updateCustomField(id, { active }),
    onSuccess: () => invalidate(),
    onError: (e) => void message.error(errorText(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteCustomField(id),
    onSuccess: () => {
      void message.success("削除した");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const columns: ColumnsType<CustomField> = [
    { title: "項目名", width: 160, dataIndex: "label" },
    {
      title: "型",
      dataIndex: "type",
      width: 320,
      render: (v: CustomField["type"], row) =>
        v === "select" ? `${TYPE_LABEL[v]}(${row.options.join(" / ")})` : TYPE_LABEL[v],
    },
    { title: "key", dataIndex: "key", width: 110, render: (v: string) => <code>{v}</code> },
    {
      title: "扱い",
      width: 280,
      render: (_: unknown, row) => (
        <Space size={4} wrap>
          {row.required && <Tag color="red">必須</Tag>}
          {row.show_in_list && <Tag color="blue">一覧に出す</Tag>}
          {row.searchable && <Tag color="purple">検索対象</Tag>}
        </Space>
      ),
    },
    { title: "並び", dataIndex: "sort", width: 90 },
    {
      title: "状態",
      dataIndex: "active",
      width: 110,
      render: (v: boolean) => (v ? <Tag color="green">稼働中</Tag> : <Tag>停止中</Tag>),
    },
    ...(canAdmin
      ? [
          {
            title: "",
            width: 200,
            render: (_: unknown, row: CustomField) => (
              <Space>
                <Button size="small" onClick={() => setEditing(row)}>
                  編集
                </Button>
                <Switch
                  size="small"
                  checked={row.active}
                  loading={toggle.isPending}
                  onChange={(v) => toggle.mutate({ id: row.id, active: v })}
                />
                <Popconfirm
                  title="この項目を消す"
                  description="値を持つ従業員が居るうちは消せない(停止で隠せる)"
                  okText="消す"
                  cancelText="やめる"
                  onConfirm={() => remove.mutate(row.id)}
                >
                  <Button size="small" danger>
                    削除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          カスタム項目
        </Typography.Title>
        {canAdmin && (
          <Button type="primary" onClick={() => setCreating(true)}>
            項目を作る
          </Button>
        )}
      </Flex>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          会社ごとに違う従業員の欄(エンド顧客・就業場所・所属会社など)をここで定義する。
          定義した項目は従業員の登録・編集フォーム、詳細、Excel 出力に出る。「一覧に出す」を付けた項目は
          従業員一覧の列にもなり、「検索対象」を付けた項目は一覧の検索語で引ける。CSV
          取込では見出しを項目名にすると取り込める。型は作った後に変えられない。
        </Typography.Text>
      </Card>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="カスタム項目を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Table<CustomField>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        pagination={false}
      />

      <Modal
        open={open}
        title={editing ? `項目の編集(${editing.label})` : "項目を作る"}
        okText="保存"
        cancelText="やめる"
        confirmLoading={save.isPending}
        onCancel={close}
        onOk={() => {
          void form.validateFields().then((v) => save.mutate({ v, target: targetKey }));
        }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="label" label="項目名" rules={[{ required: true, message: "項目名は必須" }]}>
            <Input maxLength={100} placeholder="就業場所" />
          </Form.Item>
          <Form.Item
            name="type"
            label="型"
            initialValue="text"
            extra={editing ? "型は作った後に変えられない" : undefined}
          >
            <Select
              disabled={editing !== null}
              options={(Object.keys(TYPE_LABEL) as CustomField["type"][]).map((t) => ({
                value: t,
                label: TYPE_LABEL[t],
              }))}
            />
          </Form.Item>
          {type === "select" && (
            <Form.Item
              name="options"
              label="選択肢"
              rules={[{ required: true, message: "選択肢を 1 つ以上" }]}
              extra="入力して Enter で 1 つ追加"
            >
              <Select mode="tags" open={false} tokenSeparators={[",", "、"]} placeholder="自社、A社 …" />
            </Form.Item>
          )}
          <Form.Item name="required" label="必須" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item
            name="show_in_list"
            label="一覧に出す"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
          <Form.Item name="searchable" label="検索対象" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
          <Form.Item name="sort" label="並び(小さいほど先)" initialValue={0}>
            <InputNumber style={{ width: "100%" }} min={0} max={9999} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
